import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Page, Text, View, Document, StyleSheet, pdf, Line, Svg, Image } from '@react-pdf/renderer';

import { useAppSelector } from '~redux/hooks';

import ActionButton from '~components/atoms/ActionButton';
import { TGetInventoryListing, TGetSerialNumberListingData, TInventoryTransactionListing } from '~types/feat/gold';
import { SerialNumberStatusEnum } from '~utils/enums';

type PageProps = {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    copy: string;
}

type ReportsInventoryPDFProps = {
    serialNumberListing: TGetSerialNumberListingData[];
    branchInventoryListing: TGetInventoryListing[];
}

const BranchInventoryExportPDF = (props: ReportsInventoryPDFProps) => {
    const { serialNumberListing, branchInventoryListing } = props;

    const branchListing = useAppSelector((state) => state.branch.allBranches);
    const userDetails = useAppSelector((state) => state.auth.userDetails);

    const [loading, setLoading] = useState(false);
    const userBranchDetails = branchListing.find(branch => branch.branchCode === userDetails.branchCode);

    const safeBranchInventoryListing = branchInventoryListing ?? [];
    const safeSerialNumberListing = serialNumberListing ?? [];

    const physicalInventorySummaryTableData = safeBranchInventoryListing.map(item => {
        return {
            productName: item.productName,
            quantity: item.amount,
            totalWeight: item.physicalGoldWeight,
        };
    });
    const branchesList = useAppSelector((state) => state.branch.allBranches);

    const filters = useAppSelector((state) => state.gold.goldInventoryReport.branchInventory.filters);
    const filteredSerialNumbers = filters.branchName
        ? safeSerialNumberListing.filter((item) => item.branchName === filters.branchName)
        : safeSerialNumberListing;
    const filteredBranchDetails = branchesList.find(branch => branch.branchName === filters.branchName);

    const displayBranchDetails = {
        branchName: filteredBranchDetails?.branchName ?? 'All Branches',
        branchCode: filteredBranchDetails?.branchCode ?? '-',
    };

    const productTableData = filteredSerialNumbers.map(item => {
        return {
            productName: item.productName,
            serialNo: item.serialNo,
            status: SerialNumberStatusEnum[item.status],
        };
    });

    const customerDetails = {
        name: userDetails.username,
        idNo: userDetails.userId,
    };

    const totalQuantity = physicalInventorySummaryTableData.reduce((acc, item) => acc + item.quantity, 0);
    const totalWeight = physicalInventorySummaryTableData.reduce((acc, item) => acc + item.totalWeight, 0);
    const PageWithHeaderFooter = ({
        children,
        title,
        subtitle,
        copy,
    }: PageProps) => (
        <Page size='A4' style={copy === 'Customer\'s Copy' ? styles.customerPage : styles.page} orientation='landscape'>
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>{title}</Text>
                </View>

                <View style={styles.detailsTable}>
                    <View style={styles.detailsRow}>
                        <Text style={styles.detailsCell}>Branch Name</Text>
                        <Text style={styles.detailsCell}>{displayBranchDetails?.branchName}</Text>
                    </View>

                    <View style={styles.detailsRow}>
                        <Text style={styles.detailsCell}>Branch Code</Text>
                        <Text style={styles.detailsCell}>{displayBranchDetails?.branchCode}</Text>
                    </View>

                    <View style={styles.detailsRow}>
                        <Text style={styles.detailsCell}>Date and Time</Text>
                        <Text style={styles.detailsCell}>{dayjs().format('DD MMM YYYY hh:mm A')}</Text>
                    </View>
                </View>
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {children}
            </View>
        </Page>
    );

    const renderSummaryTable = () => {
        return (
            <View style={styles.summaryContainer}>
                <Text style={styles.headerSubtitle}>Physical Inventory Summary</Text>

                <View style={styles.summaryTable}>
                    <View style={styles.summaryTableRow}>
                        <Text style={styles.summaryHeaderCell}>Product</Text>
                        <Text style={styles.summaryHeaderCellPrice}>Quantity</Text>
                        <Text style={styles.summaryHeaderCellPrice}>Total Grams</Text>
                    </View>

                    {
                        physicalInventorySummaryTableData.map((gold, index) => (
                            <View key={index} style={styles.summaryTableRow}>
                                <Text style={[styles.summaryTableCell, { width: '60%', textAlign: 'left' }]}>{gold.productName}</Text>
                                <Text style={[styles.summaryTableCell, { width: '20%' }]}>{gold.quantity}</Text>
                                <Text style={[styles.summaryTableCell, { width: '20%' }]}>{gold.totalWeight}</Text>
                            </View>
                        ))
                    }

                    <View style={styles.summaryTableRow}>
                        <Text style={[styles.summaryHeaderCell, { textAlign: 'left' }]}>Total</Text>
                        <Text style={styles.summaryHeaderCellPrice}>{totalQuantity}</Text>
                        <Text style={styles.summaryHeaderCellPrice}>{totalWeight}</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderInventoryTable = () => {
        return (
            <View style={styles.summaryContainer}>
                <Text style={styles.headerSubtitle}>Physical Inventory Detail</Text>

                <View style={styles.summaryTable}>
                    <View style={styles.summaryTableRow} fixed>
                        <Text style={styles.summaryHeaderCell}>Product</Text>
                        <Text style={styles.summaryHeaderCellPrice}>Serial #</Text>
                        <Text style={styles.summaryHeaderCellPrice}>Status</Text>
                    </View>

                    {
                        productTableData?.map((gold, index) => (
                            <View key={index} style={styles.summaryTableRow} wrap={false}>
                                <Text style={[styles.summaryTableCell, { width: '60%', textAlign: 'left' }]}>{gold.productName}</Text>
                                <Text style={[styles.summaryTableCell, { width: '20%' }]}>{gold.serialNo}</Text>
                                <Text style={[styles.summaryTableCell, { width: '20%' }]}>{gold.status}</Text>
                            </View>
                        ))
                    }
                </View>
            </View>
        );
    };

    const renderSignature = () => {
        return (
            <View style={styles.signatureContainer}>
                <View style={styles.remarksContainer}>
                    <Text>Remark:</Text>
                </View>

                <View style={styles.signatureTable}>
                    <View style={styles.signatureRow}>
                        <View>
                            <Text>Initiated by</Text>
                        </View>
                        <View style={styles.signatureBox}>
                            <Svg height='1' width='500'>
                                <Line
                                    x1='0'
                                    y1='0'
                                    x2='150'
                                    y2='0'
                                    strokeWidth={2}
                                    stroke='rgb(0, 0, 0)'
                                />
                            </Svg>
                        </View>
                        <View>
                            <Text>Name:</Text>
                        </View>
                        <View>
                            <Text>Date:</Text>
                        </View>
                    </View>
                    <View style={styles.signatureRow}>
                        <View>
                            <Text>Witnessed by (1)</Text>
                        </View>
                        <View style={styles.signatureBox}>
                            <Svg height='1' width='500'>
                                <Line
                                    x1='0'
                                    y1='0'
                                    x2='150'
                                    y2='0'
                                    strokeWidth={2}
                                    stroke='rgb(0, 0, 0)'
                                />
                            </Svg>
                        </View>
                        <View>
                            <Text>Name:</Text>
                        </View>
                        <View>
                            <Text>Date:</Text>
                        </View>
                    </View>
                    <View style={styles.signatureRow}>
                        <View>
                            <Text>Witnessed by (2)</Text>
                        </View>
                        <View style={styles.signatureBox}>
                            <Svg height='1' width='500'>
                                <Line
                                    x1='0'
                                    y1='0'
                                    x2='150'
                                    y2='0'
                                    strokeWidth={2}
                                    stroke='rgb(0, 0, 0)'
                                />
                            </Svg>
                        </View>
                        <View>
                            <Text>Name:</Text>
                        </View>
                        <View>
                            <Text>Date:</Text>
                        </View>
                    </View>
                </View>
            </View>
        );
    };

    const handleOpenPDFInNewTab = () => {
        setLoading(true);
        // Create the PDF document

        const docTitle = 'Branch Physical Inventory Report';

        const doc = (
            <Document>
                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='SUMMARY DETAILS / RINGKASAN'
                    copy={'Customer\'s Copy'}
                >
                    {renderSummaryTable()}
                    {renderInventoryTable()}
                    {renderSignature()}
                </PageWithHeaderFooter>

                {/* <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='SUMMARY DETAILS / RINGKASAN'
                    copy={'Bank\'s Copy'}
                >
                    {renderDetails()}
                    {renderGoldDescription()}
                    {renderDeclaration()}
                    {renderSignature()}
                    <View style={styles.extraSignatureContainer}>
                        {renderExtraSignature()}
                        {renderExtraSignature()}
                    </View>
                    {renderOfficeInputs()}
                </PageWithHeaderFooter> */}
            </Document>
        );

        // Generate PDF Blob and open in new tab
        pdf(doc).toBlob().then(() => {
            const promise = pdf(doc).toBlob();
            promise.then((blob) => {
                const pdfUrl = URL.createObjectURL(blob);
                setLoading(false);
                window.open(pdfUrl, '_blank');
            }).catch(() => {
                setLoading(false);
            });
        });
    };
    return (
        <div>
            <ActionButton
                label='Export to PDF'
                onClick={handleOpenPDFInNewTab}
                isLoading={loading}
                disabled={safeSerialNumberListing.length < 1}
            // className={SummaryStyles.container}
            />
        </div>
    );
};

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        paddingTop: 50, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    customerPage: {
        flexDirection: 'column',
        paddingTop: 100, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    header: {
        position: 'absolute',
        top: 10,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerContent: {
        flexDirection: 'column',
        gap: 3,
        marginBottom: 5,
    },
    headerTitle: {
        fontSize: 18,
        fontFamily: 'Helvetica-Bold',
    },
    headerSubtitle: {
        fontSize: 16,
        fontFamily: 'Helvetica-Bold',
        paddingBottom: 10,
    },
    headerCopy: {
        position: 'absolute',
        top: 5,
        right: 20,
        fontSize: 6,
        color: '#666',
    },
    divider: {
        height: 2,
        backgroundColor: '#000',
        marginVertical: 3,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    footerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    pageNumber: {
        fontSize: 6,
        color: '#666',
    },
    mainContent: {
        flex: 1,
    },
    detailsTable: {
        display: 'flex',
        width: '100%',
        paddingVertical: 15,
        paddingHorizontal: 10,
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        gap: 2,
        marginBottom: 15,
    },
    detailsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    detailsCell: {
        width: '50%',
    },
    summaryContainer: {
        marginBottom: 20,
    },
    summaryTable: {
        width: '100%',
        borderStyle: 'solid',
        borderColor: '#D4D4D8',
        borderTopWidth: 2,
        fontSize: 8,
    },
    summaryTableRow: {
        flexDirection: 'row',
        paddingLeft: 15,
        borderBottomWidth: 2,
        borderColor: '#D4D4D8',
    },
    summaryTableCell: {
        paddingVertical: 5,
        textAlign: 'center',
    },
    summaryHeaderCell: {
        width: '60%', // First column
        paddingVertical: 5,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
    },
    summaryHeaderCellPrice: {
        width: '20%', // Second column
        paddingVertical: 5,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
    },
    remarksContainer: {
        display: 'flex',
        width: '100%',
        height: 30,
        fontSize: 6,
        gap: 1,
        marginBottom: 10,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
    },
    signatureContainer: {
        // display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureTable: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureRow: {
        width: '25%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    signatureBox: {
        paddingTop: 30,
    },
});

export default BranchInventoryExportPDF;
